/** Identifiants purs communs au brief DEV et aux deux Studios. */

/** Miroir du schema de champs, confronte a celui-ci par champs.test.mjs. */
export const FAMILLE_A_DECIDER = '@a-decider';

/** `Tarif / jour` -> `tarifJour`. @param {string} nom @returns {string} */
export function clefDeChamp(nom) {
  const mots = String(nom || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (mots.length === 0) return '';
  const [tete, ...suite] = mots;
  const clef =
    tete.toLowerCase() + suite.map((m) => m[0].toUpperCase() + m.slice(1).toLowerCase()).join('');
  return /^[a-z]/.test(clef) ? clef : `champ${clef[0].toUpperCase()}${clef.slice(1)}`;
}

/** `@a-decider/<id de porte>`. @param {string} porteId @returns {string} */
export function familleADeciderDe(porteId) {
  return `${FAMILLE_A_DECIDER}/${porteId}`;
}

/** @param {string} famille @returns {boolean} */
export function estFamilleADecider(famille) {
  const f = String(famille || '');
  return f === FAMILLE_A_DECIDER || f.startsWith(`${FAMILLE_A_DECIDER}/`);
}

/** L'id kebab-case propose depuis un libelle. @param {string} texte @returns {string} */
export function kebab(texte) {
  return String(texte || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * LES MOTS DU REGLAGE « AU CLIC SUR UNE FICHE » (`portes[].auClic`), ecrits une
 * fois pour le brief et pour les lignes du Studio d'une app lue : deux ecrans,
 * un seul vocabulaire.
 */
export const MOTS_AU_CLIC = Object.freeze({
  reglage: 'Au clic sur une fiche',
  fiche: 'Ouvrir la fiche',
  vue: "Vue d'arrivée du clic",
  arriveeLiee: 'Arrivée de la porte liée',
  /* Chantier `fiche-suivi` : la fiche s'ouvre dans une vue de fiche, ou dans
     une autre porte (de cette app ou d'une autre). Le nom suit. */
  vueDeFiche: 'Ouvrir sur',
  autrePorte: 'Ouvrir dans',
});

/**
 * LES MOTS DE « EN TÊTE DE FICHE » (`portes[].etapes`, chantier `fiche-suivi`) :
 * les champs a valeurs montres dans la rangee 3 d'une fiche, une LISTE
 * d'en-tetes (« En tête 1 », « En tête 2 »), chacun son champ et son mode,
 * ecrits une fois pour le brief, les lignes du Studio d'une app lue et les
 * Parametres. `flux` et `selection` sont les deux modes de l'atelier.
 */
export const MOTS_EN_TETE = Object.freeze({
  reglage: 'En tête de fiche',
  aucun: 'Aucun',
  champ: 'Champ',
  mode: 'Affichage',
  flux: 'Étapes',
  selection: 'Sélection',
  /** La legende d'un en-tete, par son rang (a partir de 0). */
  rang: (/** @type {number} */ rang) => `En tête ${rang + 1}`,
  ajouter: 'Ajouter un en-tête',
  retirer: 'Retirer',
  /* Les ETATS (jamais un mode d'emploi) : plus de champ libre, aucun champ liste. */
  complet: 'Tous les champs sont en tête',
  sansListe: 'Aucun champ liste à choix unique',
  retire: 'Champ retiré',
  /* LES VERROUS d'un en-tete (`etapes-verrou`) : des ETIQUETTES, jamais un mode d'emploi. */
  verrou: 'Verrou',
  verrouAjouter: 'Ajouter un verrou',
  verrouRetirer: 'Retirer le verrou',
  verrouQuand: 'Quand',
  verrouFerme: 'Ferme',
  verrouToute: 'Toute la piste',
  verrouSensAvant: 'Avant',
  verrouSensDepuis: 'À partir de',
  verrouValeur: 'Valeur',
  verrouRaison: 'Raison',
  verrouRaisonExemple: 'ex. : Clôturée',
  verrouValeurRetiree: 'Valeur retirée',
});

/**
 * LES MOTS DES VUES DE FICHE (`portes[].vuesFiche`, `ecran.vuesHtml`) : la vue
 * « Fiche » du socle est toujours la, cochee, et ne se retire pas.
 */
export const MOTS_VUES_FICHE = Object.freeze({
  reglage: 'Vues de fiche',
  fiche: 'Fiche',
  toujours: 'Toujours montrée',
  /* Le niveau 4 du sur-mesure : une fiche en TypeScript, par l'agent. */
  surMesure: 'Fiche sur mesure',
  html: 'Vue HTML',
  retirer: 'Retirer',
  aRegler: 'À régler',
  /* La simulation, en mots : « Fiche résumée activée sur Projets ». Sans
     clef, le defaut du socle : les vues HTML du jeu, aucune vue Canvas. */
  activee: 'activée sur',
  desactivee: 'désactivée sur',
  parDefaut: 'par défaut',
});

/**
 * LES MOTS DU REGLAGE « DANS LA NAVIGATION » (`portes[].navigation`), ecrits une
 * fois pour le brief et pour les lignes du Studio d'une app lue. `arrivee` et
 * `sansLien` disent pourquoi une porte ne peut pas quitter le menu : ce sont
 * les deux refus du schema (`appScreensSchema`).
 */
export const MOTS_NAVIGATION = Object.freeze({
  reglage: 'Dans la navigation',
  aide: 'Décochée : hors du menu, ouverte par un lien',
  arrivee: "Porte d'arrivée : toujours dans le menu",
  sansLien: 'Aucune porte ne la lie : reste dans le menu',
});

/**
 * LA VUE D'ARRIVEE QUE LE CLIC PREND QUAND ON CHOISIT UN LIEN : le Kanban si la
 * porte liee le monte (le geste « a la Odoo » pour lequel le reglage existe),
 * sinon son arrivee (`''`). Un seul endroit, pour le formulaire du brief et
 * pour les lignes du Studio d'une app lue (donc le MCP) : revenir a « Ouvrir
 * ses taches » apres « Ouvrir la fiche » retrouve le Kanban.
 * @param {readonly string[]} vuesDeLaPorteLiee @returns {string}
 */
export function vueParDefautDuClic(vuesDeLaPorteLiee) {
  return vuesDeLaPorteLiee.includes('core.kanban') ? 'core.kanban' : '';
}

/**
 * « Ouvrir ses tâches » : le choix qui entre dans la porte liee. L'initiale du
 * nom passe en minuscule, sauf sur un sigle (« CRM » reste « CRM »).
 * @param {string} nomDeLaPorte @returns {string}
 */
export function motOuvrirSes(nomDeLaPorte) {
  const nom = String(nomDeLaPorte || '').trim();
  const sigle = nom.length > 1 && nom[1] !== nom[1].toLowerCase();
  return `Ouvrir ses ${sigle ? nom : nom.charAt(0).toLowerCase() + nom.slice(1)}`;
}
